import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import { greenRedYellowResultTypeCategory } from "@repo/app-types/entities/outcome";
import {
  isNaOutcomeKey,
  type OutcomeDistributionByScore,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  outcomeColorSchemeMap,
  type ChartColorScheme,
} from "@repo/ui/Charts/analyticsColors";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { AnchoredTooltip } from "@repo/ui/tooltip/anchoredTooltip";
import type { FC } from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";

const grayOutcomeColorScheme: ChartColorScheme = {
  fill: "var(--color-gray-300)",
  stroke: "var(--color-gray-400)",
  dot: "var(--color-gray-500)",
};

const getOutcomeColors = (category: string): ChartColorScheme =>
  outcomeColorSchemeMap[category] ?? grayOutcomeColorScheme;

// Category sort order: green → yellow → red
const categoryOrder: Record<string, number> = {
  [greenRedYellowResultTypeCategory.green]: 0,
  [greenRedYellowResultTypeCategory.yellow]: 1,
  [greenRedYellowResultTypeCategory.red]: 2,
};

// Chart layout constants
const PADDING = { left: 30, right: 10, top: 10, bottom: 32 };

// Score labels by grading type
const scoreConfig = {
  yes_no: { keys: ["no", "yes"], labels: ["No", "Yes"] },
  "1_5_score": {
    keys: ["1", "2", "3", "4", "5"],
    labels: ["1", "2", "3", "4", "5"],
  },
} as const;

interface OutcomeByCriteriaScoreChartProps {
  outcomeDefinition: OutcomeDefinition;
  /** The distribution for the specific criterion being displayed */
  criterionDistribution: OutcomeDistributionByScore | undefined;
  /** The grading value type - determines whether to show Yes/No or 1-5 on the X-axis */
  gradingValueType: "yes_no" | "1_5_score";
}

export const OutcomeByCriteriaScoreChart: FC<
  OutcomeByCriteriaScoreChartProps
> = ({ outcomeDefinition, criterionDistribution, gradingValueType }) => {
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(400);
  const [height, setHeight] = useState(100);
  // SVG shapes can't be React Aria tooltip triggers, so the chart tracks the
  // hovered segment itself and anchors one tooltip to it.
  const hoveredSegmentRef = useRef<SVGRectElement | null>(null);
  const [hoveredTooltipContent, setHoveredTooltipContent] = useState<
    string | null
  >(null);

  // Get sorted outcome metadata (excluding gray/N/A outcomes)
  const outcomes = useMemo(
    () =>
      outcomeDefinition.results
        .filter((r) => r.category !== greenRedYellowResultTypeCategory.gray)
        .sort(
          (a, b) =>
            (categoryOrder[a.category] ?? 3) - (categoryOrder[b.category] ?? 3),
        )
        .map((r) => ({
          value: r.value,
          label: r.label,
          colors: getOutcomeColors(r.category),
        })),
    [outcomeDefinition.results],
  );

  // Build chart data from criterion distribution (excluding N/A outcomes)
  const chartData = useMemo(() => {
    if (!criterionDistribution) return [];

    const { keys, labels } = scoreConfig[gradingValueType];

    return keys.map((scoreKey, index) => {
      const distribution = criterionDistribution[scoreKey] ?? {};

      // Calculate total excluding N/A outcomes
      let total = 0;
      for (const [outcomeKey, count] of Object.entries(distribution)) {
        if (!isNaOutcomeKey(outcomeKey)) {
          total += count ?? 0;
        }
      }

      const segments = outcomes.map((outcome) => {
        const count = distribution[outcome.value] ?? 0;
        return {
          outcome,
          count,
          percentage: total > 0 ? (count / total) * 100 : 0,
        };
      });

      return { score: labels[index], total, segments };
    });
  }, [criterionDistribution, outcomes, gradingValueType]);

  // Check if there's any data to show
  const hasData = chartData.some((point) => point.total > 0);

  // Handle container resize
  useLayoutEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    const updateSize = () => {
      setWidth(container.clientWidth);
      setHeight(container.clientHeight);
    };
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [hasData]);

  if (!hasData) {
    return (
      <div className="flex h-20 items-center justify-center text-sm text-muted-foreground">
        No outcome data available for this criteria
      </div>
    );
  }

  // Calculate chart dimensions
  const chartWidth = width - PADDING.left - PADDING.right;
  const chartHeight = height - PADDING.top - PADDING.bottom;
  const barWidth = chartWidth / chartData.length;
  const barGap = barWidth * 0.25;
  const actualBarWidth = barWidth - barGap;

  return (
    <div className="space-y-2">
      <Label size={Size.SMALL}>Outcome by criteria score</Label>

      {/* Chart */}
      <div
        ref={svgContainerRef}
        className="relative w-full"
        style={{ height: "92px" }}
      >
        <svg
          className="absolute size-full"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "visible" }}
        >
          {/* Grid lines */}
          <line
            x1={PADDING.left}
            y1={PADDING.top + chartHeight}
            x2={width - PADDING.right}
            y2={PADDING.top + chartHeight}
            stroke="#e5e7eb"
            strokeWidth="1"
          />
          <line
            x1={PADDING.left}
            y1={PADDING.top}
            x2={width - PADDING.right}
            y2={PADDING.top}
            stroke="#e5e7eb"
            strokeWidth="1"
            strokeDasharray="3 3"
          />

          {/* Stacked bars */}
          {chartData.map((dataPoint, barIndex) => {
            const x = PADDING.left + barIndex * barWidth + barGap / 2;
            let yOffset = 0;

            // Filter to visible segments for rounding logic
            const visibleSegments = dataPoint.segments.filter(
              (s) => s.count > 0,
            );

            return (
              <g key={dataPoint.score}>
                {dataPoint.segments.map((segment, segmentIndex) => {
                  if (segment.count === 0) return null;

                  // Height based on percentage (0-100%)
                  const segmentHeight =
                    (segment.percentage / 100) * chartHeight;
                  const y = PADDING.top + chartHeight - yOffset - segmentHeight;
                  yOffset += segmentHeight;

                  // Round corners on first/last visible segments
                  const visibleIndex = visibleSegments.indexOf(segment);
                  const isFirstVisible = visibleIndex === 0;
                  const isLastVisible =
                    visibleIndex === visibleSegments.length - 1;
                  const hasRounding = isFirstVisible || isLastVisible;

                  return (
                    <rect
                      key={segmentIndex}
                      x={x}
                      y={y}
                      width={actualBarWidth}
                      height={Math.max(segmentHeight, 0)}
                      fill={segment.outcome.colors.fill}
                      stroke={segment.outcome.colors.stroke}
                      strokeWidth="1"
                      rx={hasRounding ? 2 : 0}
                      ry={hasRounding ? 2 : 0}
                      onMouseEnter={(event) => {
                        hoveredSegmentRef.current = event.currentTarget;
                        setHoveredTooltipContent(
                          `${dataPoint.score}: ${segment.outcome.label} ${segment.count} (${segment.percentage.toFixed(0)}%)`,
                        );
                      }}
                      onMouseLeave={() => setHoveredTooltipContent(null)}
                      className="cursor-default"
                    />
                  );
                })}

                {/* X-axis label */}
                <text
                  x={x + actualBarWidth / 2}
                  y={height - PADDING.bottom + 14}
                  textAnchor="middle"
                  className="fill-gray-500 text-[10px] font-medium"
                >
                  {dataPoint.score}
                </text>
              </g>
            );
          })}

          {/* Y-axis labels */}
          <text
            x={PADDING.left - 8}
            y={PADDING.top + 4}
            textAnchor="end"
            className="fill-gray-500 text-[10px] font-medium"
          >
            100%
          </text>
          <text
            x={PADDING.left - 8}
            y={PADDING.top + chartHeight}
            textAnchor="end"
            className="fill-gray-500 text-[10px] font-medium"
          >
            0%
          </text>

          {/* X-axis title */}
          <text
            x={PADDING.left + chartWidth / 2}
            y={height - 4}
            textAnchor="middle"
            className="fill-gray-500 text-[10px] font-medium"
          >
            Criteria grade
          </text>
        </svg>

        {hoveredTooltipContent !== null ? (
          <AnchoredTooltip triggerRef={hoveredSegmentRef}>
            {hoveredTooltipContent}
          </AnchoredTooltip>
        ) : null}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {outcomes.map((outcome) => (
          <div
            key={outcome.value}
            className="flex items-center gap-1 text-[0.625rem] font-medium"
          >
            <span
              className="inline-block size-2 rounded-full"
              style={{ backgroundColor: outcome.colors.dot }}
            />
            <span className="text-gray-600">{outcome.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
